import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ResetPasswordData) => Promise<void> | void;
  /** Display name of the user whose password is being reset. */
  userName: string;
  /** Optional secondary line under the name (e.g. email). */
  userSubLabel?: string;
  isLoading?: boolean;
}

export interface ResetPasswordData {
  password: string;
  /** True forces the user to change the password on next login. */
  temporary: boolean;
}

interface ValidationError {
  field: 'newPassword' | 'confirmPassword' | 'general';
  message: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const MIN_LENGTH = 6;

const validatePassword = (password: string): string[] => {
  const errors: string[] = [];
  if (password.length < MIN_LENGTH) errors.push(`Password must be at least ${MIN_LENGTH} characters`);
  if (!/[A-Z]/.test(password)) errors.push('Password must contain at least one uppercase letter');
  if (!/[0-9]/.test(password)) errors.push('Password must contain at least one number');
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) errors.push('Password must contain at least one special character');
  return errors;
};

const calculatePasswordStrength = (password: string): PasswordStrength => {
  let score = 0;
  if (password.length >= MIN_LENGTH) score++;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;
  score = Math.min(4, Math.floor(score * 0.8));

  const map: Record<number, { label: string; color: string }> = {
    0: { label: 'Very Weak', color: '#ef4444' },
    1: { label: 'Weak', color: '#f97316' },
    2: { label: 'Fair', color: '#eab308' },
    3: { label: 'Good', color: '#22c55e' },
    4: { label: 'Strong', color: '#16a34a' },
  };
  return { score, ...map[score] };
};

// ============================================================================
// ICONS
// ============================================================================

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const KeyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  backdrop: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 10040,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden' as const,
  },
  scrollWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
    boxSizing: 'border-box' as const,
  },
  modal: {
    position: 'relative' as const,
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
    width: '100%',
    maxWidth: '440px',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden' as const,
    zIndex: 10050,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.625rem 1rem',
    borderBottom: '1px solid #e2e8f0',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '10px 10px 0 0',
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'white',
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  },
  closeButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '6px',
    padding: '0.25rem',
    cursor: 'pointer',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  },
  content: {
    padding: '0.75rem 1rem',
    flex: '0 0 auto' as const,
  },
  userBanner: {
    padding: '0.5rem 0.75rem',
    backgroundColor: '#eef2ff',
    border: '1px solid #c7d2fe',
    borderRadius: '6px',
    marginBottom: '0.625rem',
  },
  userBannerLabel: {
    fontSize: '0.65rem',
    color: '#4338ca',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.125rem',
  },
  userBannerName: {
    fontSize: '0.875rem',
    color: '#1e293b',
    fontWeight: 600,
  },
  userBannerSub: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '0',
  },
  formGroup: { marginBottom: '0.625rem' },
  label: {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.25rem',
  },
  inputWrapper: { position: 'relative' as const },
  input: {
    width: '100%',
    padding: '0.5rem 2.5rem 0.5rem 0.75rem',
    fontSize: '0.875rem',
    border: '1.5px solid #e2e8f0',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box' as const,
  },
  inputError: { borderColor: '#ef4444' },
  inputFocus: {
    borderColor: '#667eea',
    boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
  },
  toggleButton: {
    position: 'absolute' as const,
    right: '0.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  strengthMeter: { marginTop: '0.25rem' },
  strengthBar: {
    height: '3px',
    backgroundColor: '#e2e8f0',
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: '0.125rem',
  },
  strengthFill: {
    height: '100%',
    transition: 'width 0.3s, background-color 0.3s',
    borderRadius: '2px',
  },
  strengthLabel: { fontSize: '0.7rem', fontWeight: 500 },
  requirements: {
    marginTop: '0.375rem',
    padding: '0.375rem 0.5rem',
    backgroundColor: '#f8fafc',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    columnGap: '0.75rem',
    rowGap: '0.125rem',
  },
  requirementItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.7rem',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '0.75rem',
    marginTop: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  temporaryRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '6px',
  },
  temporaryCheckbox: {
    marginTop: '0.125rem',
    width: '0.875rem',
    height: '0.875rem',
    cursor: 'pointer',
    flexShrink: 0,
  },
  temporaryLabel: {
    fontSize: '0.75rem',
    color: '#78350f',
    cursor: 'pointer',
    lineHeight: 1.35,
  },
  temporaryHint: {
    color: '#92400e',
    fontSize: '0.7rem',
    marginTop: '0.125rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    padding: '0.625rem 1rem',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    flexShrink: 0,
    borderRadius: '0 0 10px 10px',
  },
  button: {
    padding: '0.4rem 1rem',
    fontSize: '0.8125rem',
    fontWeight: 600,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
  },
  cancelButton: {
    backgroundColor: 'white',
    color: '#64748b',
    border: '1px solid #e2e8f0',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  },
  submitButtonDisabled: {
    background: '#cbd5e1',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
};

// ============================================================================
// COMPONENT
// ============================================================================

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userName,
  userSubLabel,
  isLoading: externalLoading,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [temporary, setTemporary] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Reset internal state every time the modal opens, so a previous attempt's
  // values don't bleed into a new user's reset session.
  useEffect(() => {
    if (isOpen) {
      setNewPassword('');
      setConfirmPassword('');
      setTemporary(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setErrors([]);
      setFocusedField(null);
      setSaveConfirmOpen(false);
      setDiscardConfirmOpen(false);
    }
  }, [isOpen]);

  // Any user input on the form counts as unsaved changes.
  const isDirty = useMemo(
    () => Boolean(newPassword) || Boolean(confirmPassword) || temporary,
    [newPassword, confirmPassword, temporary]
  );

  const passwordStrength = useMemo(
    () => calculatePasswordStrength(newPassword),
    [newPassword]
  );

  const requirements = useMemo(
    () => [
      { id: 'length', label: `At least ${MIN_LENGTH} characters`, met: newPassword.length >= MIN_LENGTH },
      { id: 'uppercase', label: 'One uppercase letter', met: /[A-Z]/.test(newPassword) },
      { id: 'number', label: 'One number', met: /[0-9]/.test(newPassword) },
      { id: 'special', label: 'One special character', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) },
    ],
    [newPassword]
  );

  // Enable as soon as both fields are non-empty and matching. Rule violations
  // (length / uppercase / number / special) are surfaced inline on submit by
  // handleSubmit -> validatePassword, so the button stays responsive.
  const isFormValid = useMemo(() => {
    return Boolean(newPassword) && newPassword === confirmPassword;
  }, [newPassword, confirmPassword]);

  const getFieldError = useCallback(
    (field: ValidationError['field']) => errors.find((e) => e.field === field)?.message,
    [errors]
  );

  // Step 1: validate, then open the save confirmation dialog.
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const newErrors: ValidationError[] = [];

      const pwdErrors = validatePassword(newPassword);
      if (pwdErrors.length > 0) {
        newErrors.push({ field: 'newPassword', message: pwdErrors[0] });
      }

      if (!confirmPassword) {
        newErrors.push({ field: 'confirmPassword', message: 'Please confirm the new password' });
      } else if (newPassword !== confirmPassword) {
        newErrors.push({ field: 'confirmPassword', message: 'Passwords do not match' });
      }

      if (newErrors.length > 0) {
        setErrors(newErrors);
        return;
      }

      setErrors([]);
      setSaveConfirmOpen(true);
    },
    [newPassword, confirmPassword]
  );

  // Step 2: user confirmed — call the API.
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSubmitting(true);
    try {
      await onSubmit({ password: newPassword, temporary });
      onClose();
    } catch (error: any) {
      setErrors([{ field: 'general', message: error?.message || 'Failed to reset password.' }]);
    } finally {
      setIsSubmitting(false);
    }
  }, [newPassword, temporary, onSubmit, onClose]);

  const handleClose = useCallback(() => {
    if (isSubmitting || externalLoading) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [isSubmitting, externalLoading, isDirty, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const loading = isSubmitting || externalLoading;

  return (
    <div style={styles.backdrop}>
      <div style={styles.scrollWrapper}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.headerTitle}>
              <KeyIcon />
              Reset Password
            </h2>
            <button style={styles.closeButton} onClick={handleClose} disabled={loading}>
              <CloseIcon />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
          >
            <div style={styles.content}>
              <div style={styles.userBanner}>
                <div style={styles.userBannerLabel}>Resetting Password For</div>
                <div style={styles.userBannerName}>{userName}</div>
                {userSubLabel && <div style={styles.userBannerSub}>{userSubLabel}</div>}
              </div>

              {getFieldError('general') && (
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    color: '#dc2626',
                    fontSize: '0.875rem',
                  }}
                >
                  {getFieldError('general')}
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>New Password</label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onFocus={() => setFocusedField('newPassword')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter new password"
                    disabled={loading}
                    autoComplete="new-password"
                    style={{
                      ...styles.input,
                      ...(getFieldError('newPassword') ? styles.inputError : {}),
                      ...(focusedField === 'newPassword' ? styles.inputFocus : {}),
                    }}
                  />
                  <button
                    type="button"
                    style={styles.toggleButton}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {getFieldError('newPassword') && (
                  <div style={styles.errorText}>{getFieldError('newPassword')}</div>
                )}

                {newPassword && (
                  <div style={styles.strengthMeter}>
                    <div style={styles.strengthBar}>
                      <div
                        style={{
                          ...styles.strengthFill,
                          width: `${(passwordStrength.score / 4) * 100}%`,
                          backgroundColor: passwordStrength.color,
                        }}
                      />
                    </div>
                    <span style={{ ...styles.strengthLabel, color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}

                <div style={styles.requirements}>
                  {requirements.map((r) => (
                    <div
                      key={r.id}
                      style={{ ...styles.requirementItem, color: r.met ? '#16a34a' : '#64748b' }}
                    >
                      {r.met ? (
                        <span style={{ color: '#16a34a' }}>
                          <CheckIcon />
                        </span>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>
                          <XIcon />
                        </span>
                      )}
                      {r.label}
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm New Password</label>
                <div style={styles.inputWrapper}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Confirm new password"
                    disabled={loading}
                    autoComplete="new-password"
                    style={{
                      ...styles.input,
                      ...(getFieldError('confirmPassword') ? styles.inputError : {}),
                      ...(focusedField === 'confirmPassword' ? styles.inputFocus : {}),
                    }}
                  />
                  <button
                    type="button"
                    style={styles.toggleButton}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {getFieldError('confirmPassword') && (
                  <div style={styles.errorText}>{getFieldError('confirmPassword')}</div>
                )}
                {confirmPassword && newPassword && confirmPassword === newPassword && (
                  <div
                    style={{
                      color: '#16a34a',
                      fontSize: '0.8125rem',
                      marginTop: '0.375rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    <CheckIcon /> Passwords match
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <div style={styles.temporaryRow}>
                  <input
                    id="reset-password-temporary"
                    type="checkbox"
                    checked={temporary}
                    onChange={(e) => setTemporary(e.target.checked)}
                    disabled={loading}
                    style={styles.temporaryCheckbox}
                  />
                  <label htmlFor="reset-password-temporary" style={styles.temporaryLabel}>
                    Require user to change password on next login
                    <div style={styles.temporaryHint}>
                      When enabled, the user will be prompted to set a new password the first time
                      they sign in.
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div style={styles.footer}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...styles.submitButton,
                  ...(!isFormValid || loading ? styles.submitButtonDisabled : {}),
                }}
                disabled={!isFormValid || loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {saveConfirmOpen && createPortal(
        <ConfirmOverlay
          title="Reset Password?"
          message={
            temporary
              ? `This will set a new password for ${userName}. They will be required to change it on next login.`
              : `This will set a new password for ${userName}.`
          }
          confirmLabel="Reset Password"
          variant="primary"
          onConfirm={handleConfirmSave}
          onCancel={() => setSaveConfirmOpen(false)}
        />,
        document.body
      )}

      {discardConfirmOpen && createPortal(
        <ConfirmOverlay
          title="Discard Changes?"
          message="You have unsaved changes. Are you sure you want to close this dialog?"
          confirmLabel="Discard"
          variant="danger"
          onConfirm={handleConfirmDiscard}
          onCancel={() => setDiscardConfirmOpen(false)}
        />,
        document.body
      )}
    </div>
  );
};

// ============================================================================
// CONFIRM OVERLAY
// ============================================================================

interface ConfirmOverlayProps {
  title: string;
  message: string;
  confirmLabel: string;
  variant: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmOverlay: React.FC<ConfirmOverlayProps> = ({
  title,
  message,
  confirmLabel,
  variant,
  onConfirm,
  onCancel,
}) => {
  const headerBg =
    variant === 'danger'
      ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
      : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)';
  const headerColor = variant === 'danger' ? '#991b1b' : '#1e3a8a';
  const headerBorder = variant === 'danger' ? '1px solid #f87171' : '1px solid #93c5fd';
  const confirmBg = variant === 'danger' ? '#dc2626' : '#2563eb';

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.55)',
          zIndex: 10060,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '10px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          zIndex: 10061,
          width: '90%',
          maxWidth: '420px',
          overflow: 'hidden',
        }}
        role="alertdialog"
        aria-modal="true"
      >
        <div
          style={{
            padding: '0.875rem 1.25rem',
            background: headerBg,
            borderBottom: headerBorder,
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: headerColor }}>
            {title}
          </h3>
        </div>
        <div style={{ padding: '1rem 1.25rem' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#475569', lineHeight: 1.5 }}>
            {message}
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.4rem 1rem',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: 'white',
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '0.4rem 1rem',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderRadius: '6px',
              border: 'none',
              background: confirmBg,
              color: 'white',
              cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordModal;
